import { Injectable } from '@angular/core';
import {
  CashManager,
  CashOperation,
  CashOperationResponse,
} from './gestorEfectivo.interface';
import { CashKeeperGestorEfectivo } from './cashKeeperGestorEfectivo';
import { AppConfigJsonGestorEfectivo } from '../../conf/DeviceConfiguration';

/** Puerto por defecto del gestor de efectivo en la misma máquina. */
const DEFAULT_CASHKEEPER_PORT = 8080;

@Injectable({
  providedIn: 'root',
})
export class GestorEfectivoService {
  private cashManager: CashManager | null = null;
  private currentManagerName: string | null = null;

/**
 * Initializes and connects the cash manager based on configuration.
 * Called from `ConfigurationService` after loading `getAppConfig` when `GestorEfectivoActivo = '1'`.
 * If `HostCashKeeper` is missing, it uses the same machine where the app runs.
 */
  async initializeCashManager(config: AppConfigJsonGestorEfectivo): Promise<boolean> {
    if (!config || config.GestorEfectivoActivo !== '1') {
      return false;
    }
    const managerName = (config.NombreGestor || 'CashKeeper').trim();
    const host = (config.HostCashKeeper || '').trim() || this.getHostSameMachine();
    await this.disconnectCashManager();
    this.cashManager = new CashKeeperGestorEfectivo();
    const ok = await this.cashManager.connect(managerName, host);
    if (ok) {
      this.currentManagerName = managerName;
    } else {
      this.cashManager = null;
    }
    return ok;
  }

  /**
   * Intenta conectar el gestor al iniciar la app (misma máquina, puerto por defecto).
   * Si la máquina CashKeeper está activa, la conexión tendrá éxito; si no, se queda sin gestor.
   * Ya no depende de configuración del API.
   */
  async tryInitializeCashManagerByDefault(): Promise<boolean> {
    if (this.isCashManagerConnected()) {
      console.log('[GestorEfectivoService] tryInitializeCashManagerByDefault: already connected, skip.');
      return true;
    }
    console.log('[GestorEfectivoService] tryInitializeCashManagerByDefault: connecting...');
    return this.initializeCashManagerParams('CashKeeper', this.getHostSameMachine());
  }

  /**
   * Initializes by parameters. If host is empty, it uses the same machine where the app runs.
   */
  async initializeCashManagerParams(managerName: string, host?: string): Promise<boolean> {
    const resolvedHost = (host || '').trim() || this.getHostSameMachine();
    await this.disconnectCashManager();
    this.cashManager = new CashKeeperGestorEfectivo();
    const ok = await this.cashManager.connect(managerName || 'CashKeeper', resolvedHost);
    if (ok) {
      this.currentManagerName = managerName || 'CashKeeper';
    } else {
      this.cashManager = null;
    }
    return ok;
  }

  /**
   * Desconecta el gestor de efectivo. Llamar al cerrar sesión o salir de la app.
   */
  async disconnectCashManager(): Promise<void> {
    if (this.cashManager) {
      console.log('[GestorEfectivoService] Disconnecting cash manager...');
      await this.cashManager.disconnect();
      this.cashManager = null;
      this.currentManagerName = null;
      console.log('[GestorEfectivoService] Cash manager disconnected.');
    } else {
      console.log('[GestorEfectivoService] disconnectCashManager: no manager connected.');
    }
  }

  /** Indicates if the cash manager is connected. */
  isCashManagerConnected(): boolean {
    return this.cashManager != null && this.cashManager.isConnected();
  }

  /** Returns the current cash manager implementation. */
  getCashManager(): CashManager | null {
    return this.cashManager;
  }

  /**
   * Executes a sale or refund operation on the cash manager (CashKeeper).
   * @param operation 'N' = sale, 'D' = refund
   * @param amountCents Amount in cents, positive (1€ = 100)
   */
  async processCashOperation(
    operation: CashOperation,
    amountCents: number
  ): Promise<CashOperationResponse> {
    console.log('[GestorEfectivoService] processCashOperation', { operation, amountCents });
    if (!this.cashManager?.isConnected()) {
      console.warn('[GestorEfectivoService] Cash manager not connected; operation not sent.');
      // Format aligned with CashKeeper normalizer (i18n by code).
      return { ok: false, error: 'ERR:NO CONECTADO' };
    }
    const res = await this.cashManager.processCashOperation(operation, amountCents);
    console.log('[GestorEfectivoService] processCashOperation result', res);
    return res;
  }

  /** Host de la misma máquina donde corre la app (para cuando no se configura HostCashKeeper). */
  private getHostSameMachine(): string {
    const w = typeof window !== 'undefined' ? window : null;
    const hostname = w?.location?.hostname || 'localhost';
    return `${hostname}:${DEFAULT_CASHKEEPER_PORT}`;
  }
}
